import React, {useEffect} from 'react';

import {useDispatch, useSelector} from 'react-redux';

import {DocumentsNotificationsProvider} from 'app/notifly/components/DocumentsNotificationsProvider';
import {PresenceSubscriber} from 'app/presence/components/PresenceSubscriber';
import {useMyTasks} from 'app/tasks/api';
import {
  setLastUsedTasksDashboard,
  toggleMyTasksCollapsed,
} from 'app/tasks/slice';
import {myTasksFlattened} from 'app/tasks/utils';
import {getTaskUrlForFilters} from 'app/urls';
import {gt} from 'app/utils/i18n';

import {MyTasksRootSection} from './MyTasksRootSection';

import type {TaskMineDashboardSerializedType} from 'sweet-types';

export const MyTasksList = (): JSX.Element | null => {
  const {data: tasks} = useMyTasks();
  const dispatch = useDispatch();

  const collapsedState = useSelector(
    ({tasks}) => tasks.dashboard.mineCollapsed
  );

  useEffect(() => {
    dispatch(setLastUsedTasksDashboard('mine'));
  }, []);

  const instancesIds = getAllTasksIds(tasks);

  return tasks == null ? null : (
    <DocumentsNotificationsProvider
      contentType="taskinstance"
      documentsIds={instancesIds}
    >
      {(tasksNotifications) => (
        <>
          <MyTasksRootSection
            title={gt('Overdue')}
            showTaskDate={true}
            collapsedState={collapsedState.overdue}
            onToggle={(subsec) =>
              dispatch(toggleMyTasksCollapsed(['overdue', subsec]))
            }
            tasksForYou={tasks.tasksDueForYou}
            tasksFromYou={tasks.tasksDueFromYou}
            notifications={tasksNotifications}
            moreUrl={getTaskUrlForFilters({
              completed: false,
              interval: 'all-time',
              order: '-due',
            })}
            isOverdueSection={true}
          />
          <MyTasksRootSection
            title={gt('Today')}
            showTaskDate={false}
            collapsedState={collapsedState.today}
            onToggle={(subsec) =>
              dispatch(toggleMyTasksCollapsed(['today', subsec]))
            }
            tasksForYou={tasks.tasksForYou}
            tasksFromYou={tasks.tasksFromYou}
            notifications={tasksNotifications}
            moreUrl={getTaskUrlForFilters({
              interval: 'today',
            })}
          />
          <MyTasksRootSection
            title={gt('Soon')}
            showTaskDate={true}
            collapsedState={collapsedState.soon}
            onToggle={(subsec) =>
              dispatch(toggleMyTasksCollapsed(['soon', subsec]))
            }
            tasksForYou={tasks.tasksSoonForYou}
            tasksFromYou={tasks.tasksSoonFromYou}
            notifications={tasksNotifications}
            moreUrl={getTaskUrlForFilters({
              interval: 'this-week',
            })}
          />
          <PresenceSubscriber
            contentType="taskinstance"
            objectsIds={instancesIds}
            stealth={true}
          />
        </>
      )}
    </DocumentsNotificationsProvider>
  );
};

function getAllTasksIds(
  state: TaskMineDashboardSerializedType | undefined
): Array<number> {
  return state == null
    ? []
    : Array.from(
        new Set(
          myTasksFlattened(state)
            .map((t) => t.id)
            .filter((id): id is number => id != null)
        )
      );
}
